import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import { toast } from 'sonner';
import {
  ArrowDownTrayIcon, CheckCircleIcon, ClockIcon,
  ExclamationCircleIcon, TicketIcon, XMarkIcon,
  ChevronDownIcon, CheckIcon,
} from '@heroicons/react/24/outline';
import { apiClient } from '../../lib/axios';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

interface SearchTask {
  id: string;
  company: string;
  location?: string;
  type: 'single' | 'bulk';
}

interface Ticket {
  id: string;
  subject: string;
  description: string;
  task_id?: string;
  task_company?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  created_at: string;
  admin_reply?: string | null;
  replied_at?: string | null;
}

interface TicketsResponse {
  data: Ticket[];
  meta: {
    total: number;
    page: number;
    per_page: number;
    last_page: number;
  };
}

export default function SupportTickets() {
  useDocumentTitle('Support Tickets', 'Submit and track your support tickets.');

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [tasks, setTasks] = useState<SearchTask[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  useEffect(() => {
    fetchTasks();
    fetchTickets();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await apiClient.get('/search/history');
      setTasks((res.data.data || []).slice(0, 50));
    } catch {
      toast.error('Failed to load search tasks.');
    }
  };

  const fetchTickets = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await apiClient.get<TicketsResponse>('/tickets', {
        params: { page: pageNum, per_page: 20 },
      });
      setTickets(res.data.data);
      setPage(pageNum);
    } catch {
      toast.error('Failed to load tickets.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !description.trim()) {
      toast.error('Subject and description are required.');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post('/tickets', {
        subject,
        description,
        priority,
        task_id: selectedTaskId || null,
      });
      toast.success('Ticket created successfully!');
      setSubject('');
      setDescription('');
      setPriority('medium');
      setSelectedTaskId('');
      await fetchTickets(1);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create ticket.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
      case 'closed':
        return <CheckCircleIcon className="w-4 h-4 text-emerald-500" />;
      case 'in_progress':
        return <ClockIcon className="w-4 h-4 text-blue-500" />;
      case 'open':
      default:
        return <TicketIcon className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace('_', ' ').charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
  };

  const getPriorityBadge = (p: string) => {
    const colors: Record<string, string> = {
      low: 'bg-blue-50 text-blue-600 border-blue-200',
      medium: 'bg-slate-50 text-slate-600 border-slate-200',
      high: 'bg-orange-50 text-orange-600 border-orange-200',
      urgent: 'bg-red-50 text-red-600 border-red-200',
    };
    return colors[p] || colors.medium;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 flex flex-col gap-6">
        {/* Heading */}
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Support Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">Submit and track your support requests.</p>
        </div>

        {/* Submit Form */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
          <h2 className="text-[13px] font-extrabold text-slate-500 uppercase tracking-wider mb-4">New Ticket</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Subject *</label>
              <input
                type="text"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Brief description of the issue"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Description *</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe the issue in detail..."
                rows={5}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 resize-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Priority</label>
                <Select.Root value={priority} onValueChange={(value) => setPriority(value as any)}>
                  <Select.Trigger className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 cursor-pointer flex items-center justify-between bg-white hover:border-slate-300 transition-colors">
                    <Select.Value />
                    <Select.Icon className="text-slate-400">
                      <ChevronDownIcon className="w-4 h-4" />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="overflow-hidden bg-white border border-slate-200 rounded-xl shadow-lg z-50">
                      <Select.Viewport className="p-1">
                        {['low', 'medium', 'high', 'urgent'].map(p => (
                          <Select.Item
                            key={p}
                            value={p}
                            className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 cursor-pointer rounded-lg data-[state=checked]:bg-brand-50 data-[state=checked]:text-brand-700 outline-none transition-colors"
                          >
                            <Select.ItemText>{p.charAt(0).toUpperCase() + p.slice(1)}</Select.ItemText>
                            <Select.ItemIndicator className="ml-auto">
                              <CheckIcon className="w-4 h-4" />
                            </Select.ItemIndicator>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-2">Job ID (Optional)</label>
                <input
                  type="text"
                  value={selectedTaskId}
                  onChange={e => setSelectedTaskId(e.target.value)}
                  placeholder="Paste full or partial job ID"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-semibold text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 hover:border-slate-300 transition-colors font-mono"
                />
                {selectedTaskId && (
                  <p className="text-[10px] text-slate-500 mt-1.5">
                    ✓ Job ID will be validated when you submit
                  </p>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-slate-900 text-sm font-bold rounded-xl transition-colors"
            >
              {submitting ? 'Submitting…' : 'Submit Ticket'}
            </button>
          </form>
        </div>

        {/* Tickets List */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-[13px] font-extrabold text-slate-500 uppercase tracking-wider">Your Tickets</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-50 bg-slate-50/40">
                  <th className="px-6 py-3">Subject</th>
                  <th className="px-6 py-3">Job Reference</th>
                  <th className="px-6 py-3">Priority</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {[1, 2, 3, 4, 5].map(j => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-slate-100 animate-pulse rounded" style={{ width: `${35 + (j % 3) * 20}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : tickets.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-10 text-center">
                      <TicketIcon className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-700 text-sm font-bold">No tickets yet</p>
                    </td>
                  </tr>
                ) : (
                  tickets.map(ticket => (
                    <tr key={ticket.id} onClick={() => setSelectedTicket(ticket)} className="hover:bg-slate-50/60 transition-colors cursor-pointer">
                      <td className="px-6 py-4">
                        <p className="text-sm font-semibold text-slate-900">{ticket.subject}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-[11px] font-mono text-slate-400">
                          {ticket.task_id ? `#${ticket.task_id.substring(0, 8).toUpperCase()} · ${ticket.task_company}` : '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-[11px] font-bold border ${getPriorityBadge(ticket.priority)}`}>
                          {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5">
                          {getStatusIcon(ticket.status)}
                          <span className="text-[11px] font-semibold text-slate-600">{getStatusLabel(ticket.status)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[11px] text-slate-400 whitespace-nowrap">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Ticket Detail Modal */}
        {selectedTicket && (
          <Dialog.Root open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
              <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                  <h2 className="text-lg font-extrabold text-slate-900">{selectedTicket.subject}</h2>
                  <button onClick={() => setSelectedTicket(null)} className="text-slate-400 hover:text-slate-700">
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 space-y-6">
                  {/* Status & Priority */}
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold border ${getPriorityBadge(selectedTicket.priority)}`}>
                      {selectedTicket.priority.charAt(0).toUpperCase() + selectedTicket.priority.slice(1)}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {getStatusIcon(selectedTicket.status)}
                      <span className="text-[12px] font-semibold text-slate-600">{getStatusLabel(selectedTicket.status)}</span>
                    </div>
                    <span className="text-[11px] text-slate-400 ml-auto">
                      {new Date(selectedTicket.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>

                  {/* Description */}
                  <div>
                    <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">Description</p>
                    <p className="text-[13px] text-slate-700 whitespace-pre-wrap break-words leading-relaxed bg-slate-50 p-4 rounded-xl">
                      {selectedTicket.description}
                    </p>
                  </div>

                  {/* Admin Reply */}
                  {selectedTicket.admin_reply ? (
                    <div>
                      <p className="text-[11px] font-extrabold text-emerald-600 uppercase tracking-wider mb-2">Admin Reply</p>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <p className="text-[13px] text-emerald-900 whitespace-pre-wrap break-words leading-relaxed">
                          {selectedTicket.admin_reply}
                        </p>
                        {selectedTicket.replied_at && (
                          <p className="text-[10px] text-emerald-600 mt-3 font-medium">
                            Replied on {new Date(selectedTicket.replied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-[12px] text-slate-500 font-medium">Awaiting admin response...</p>
                    </div>
                  )}
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        )}
      </div>
    </div>
  );
}
